import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Badge, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import apiService from '../../services/api';

const ReconciliationReportTypeManagement = ({ organization, onBack }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchName, setSearchName] = useState('');
    const [searchCode, setSearchCode] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        item_start_row: 1,
        ordinal: 0,
        structure: '{}',
        is_active: true
    });

    useEffect(() => {
        if (organization) {
            loadItems();
        }
    }, [organization]);

    const loadItems = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiService.getReconciliationReportTypesByOrganization(organization.id);
            if (response.success) {
                setItems(response.data || []);
            } else {
                setError(response.error || 'Failed to load reconciliation report types');
                setItems([]);
            }
        } catch (err) {
            console.error('Error loading reconciliation report types:', err);
            setError('Failed to load reconciliation report types');
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getFilteredAndSorted = () => {
        let filtered = items.filter(item => {
            const nameMatch = !searchName || (item.name && item.name.toLowerCase().includes(searchName.toLowerCase()));
            const codeMatch = !searchCode || (item.code && item.code.toLowerCase().includes(searchCode.toLowerCase()));
            return nameMatch && codeMatch;
        });

        if (!sortConfig.key) return filtered;

        return [...filtered].sort((a, b) => {
            let aVal = a[sortConfig.key] ?? '';
            let bVal = b[sortConfig.key] ?? '';
            if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.8rem' }}></i>;
        }
        return sortConfig.direction === 'asc' ?
            <i className="bi bi-arrow-up ms-1"></i> :
            <i className="bi bi-arrow-down ms-1"></i>;
    };

    const handleShowModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                name: item.name || '',
                code: item.code || '',
                item_start_row: item.item_start_row ?? 1,
                ordinal: item.ordinal ?? 0,
                structure: typeof item.structure === 'object' ? JSON.stringify(item.structure, null, 2) : (item.structure || '{}'),
                is_active: item.is_active ?? true
            });
        } else {
            setEditingItem(null);
            setFormData({ name: '', code: '', item_start_row: 1, ordinal: 0, structure: '{}', is_active: true });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({ name: '', code: '', item_start_row: 1, ordinal: 0, structure: '{}', is_active: true });
        setError('');
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) { setError('Name is required'); return; }
        if (!formData.code.trim()) { setError('Code is required'); return; }
        if (!formData.item_start_row || formData.item_start_row < 1) { setError('Item start row must be at least 1'); return; }

        let parsedStructure;
        try {
            parsedStructure = JSON.parse(formData.structure);
        } catch {
            setError('Structure must be valid JSON');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const data = {
                organization_id: organization.id,
                name: formData.name.trim(),
                code: formData.code.trim().toUpperCase(),
                item_start_row: parseInt(formData.item_start_row, 10),
                ordinal: parseInt(formData.ordinal, 10) || 0,
                structure: parsedStructure,
                is_active: formData.is_active
            };

            let response;
            if (editingItem) {
                response = await apiService.updateReconciliationReportType(editingItem.id, data);
            } else {
                response = await apiService.createReconciliationReportType(data);
            }

            if (response.success) {
                handleCloseModal();
                await loadItems();
            } else {
                setError(response.error || 'Failed to save reconciliation report type');
            }
        } catch (err) {
            console.error('Error saving reconciliation report type:', err);
            setError('Failed to save reconciliation report type');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <Button variant="outline-secondary" onClick={onBack} className="me-3">
                        <i className="bi bi-arrow-left me-1"></i> Back
                    </Button>
                    <span className="fs-4 fw-bold">
                        <i className="bi bi-file-earmark-bar-graph me-2"></i>Reconciliation Report Type Management
                    </span>
                    <p className="text-muted mt-2 mb-0">Manage reconciliation report types for {organization.name}</p>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>{error}
                </Alert>
            )}

            <Card className="shadow-sm">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0"><i className="bi bi-list-check me-2"></i>Reconciliation Report Types</h5>
                        <div className="d-flex align-items-center">
                            {!loading && items.length > 0 && (
                                <Badge bg="info" className="me-3">{getFilteredAndSorted().length} / {items.length}</Badge>
                            )}
                            <Button variant="primary" size="sm" onClick={() => handleShowModal()}>
                                <i className="bi bi-plus-circle me-1"></i> Add Report Type
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" /><p className="mt-3 text-muted">Loading...</p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className="bi bi-file-earmark-bar-graph display-1"></i>
                            <div className="mt-2">No reconciliation report types found for this organization</div>
                        </div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold"><i className="bi bi-search me-1"></i>Search by Name</Form.Label>
                                        <Form.Control type="text" placeholder="Enter name..." value={searchName} onChange={(e) => setSearchName(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold"><i className="bi bi-search me-1"></i>Search by Code</Form.Label>
                                        <Form.Control type="text" placeholder="Enter code..." value={searchCode} onChange={(e) => setSearchCode(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            {(searchName || searchCode) && (
                                <div className="mb-3">
                                    <Button variant="outline-secondary" size="sm" onClick={() => { setSearchName(''); setSearchCode(''); }}>
                                        <i className="bi bi-x-circle me-1"></i>Clear Filters
                                    </Button>
                                </div>
                            )}
                            <Table striped bordered hover responsive>
                                <thead className="table-dark">
                                    <tr>
                                        <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Name {getSortIcon('name')}</th>
                                        <th onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>Code {getSortIcon('code')}</th>
                                        <th onClick={() => handleSort('item_start_row')} style={{ cursor: 'pointer' }}>Item Start Row {getSortIcon('item_start_row')}</th>
                                        <th onClick={() => handleSort('ordinal')} style={{ cursor: 'pointer' }}>Ordinal {getSortIcon('ordinal')}</th>
                                        <th onClick={() => handleSort('is_active')} style={{ cursor: 'pointer' }}>Active {getSortIcon('is_active')}</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getFilteredAndSorted().map(item => (
                                        <tr key={item.id}>
                                            <td>{item.name || '-'}</td>
                                            <td><code className="text-primary">{item.code || '-'}</code></td>
                                            <td>{item.item_start_row}</td>
                                            <td>{item.ordinal != null ? item.ordinal : '-'}</td>
                                            <td><Badge bg={item.is_active ? 'success' : 'secondary'}>{item.is_active ? 'Yes' : 'No'}</Badge></td>
                                            <td>
                                                <Button variant="outline-primary" size="sm" title="Edit" onClick={() => handleShowModal(item)}>
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem ? 'Edit Report Type' : 'Add Report Type'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        {error && <Alert variant="danger" dismissible onClose={() => setError('')}><i className="bi bi-exclamation-triangle me-2"></i>{error}</Alert>}
                        <Form.Group className="mb-3">
                            <Form.Label>Organization</Form.Label>
                            <Form.Control type="text" value={organization?.name || ''} disabled readOnly />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter name" required maxLength={255} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Code *</Form.Label>
                            <Form.Control type="text" name="code" value={formData.code} onChange={handleInputChange} placeholder="Enter code" required maxLength={255} style={{ textTransform: 'uppercase' }} disabled={editingItem !== null} />
                            <Form.Text className="text-muted">{editingItem ? 'Code cannot be changed once created.' : 'Will be converted to uppercase. Must be unique per organization.'}</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Item Start Row *</Form.Label>
                            <Form.Control type="number" name="item_start_row" value={formData.item_start_row} onChange={handleInputChange} min={1} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Ordinal</Form.Label>
                            <Form.Control type="number" name="ordinal" value={formData.ordinal} onChange={handleInputChange} min={0} step={1} placeholder="Enter display ordinal (e.g., 0, 1, 2)" />
                            <Form.Text className="text-muted">Numeric value used to control the display order.</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Structure (JSON) *</Form.Label>
                            <Form.Control as="textarea" rows={6} name="structure" value={formData.structure} onChange={handleInputChange} placeholder='{"column1": "type", ...}' required style={{ fontFamily: 'monospace' }} />
                            <Form.Text className="text-muted">Must be valid JSON.</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} label="Active" />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving...</> : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default ReconciliationReportTypeManagement;
